import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown } from 'lucide-react';

export type DatePreset =
  | 'today' | 'yesterday' | 'last-7-days' | 'last-month'
  | 'this-month' | 'this-year' | 'custom';

export interface DateRange { startDate: string; endDate: string }

interface Props {
  value: DateRange;
  onChange: (range: DateRange & { preset: DatePreset }) => void;
  defaultPreset?: DatePreset;
  size?: 'sm' | 'md';
  className?: string;
}

const PRESETS: Array<{ id: DatePreset; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last-7-days', label: 'Last 7 Days' },
  { id: 'last-month', label: 'Last Month' },
  { id: 'this-month', label: 'This Month' },
  { id: 'this-year', label: 'This Year' },
  { id: 'custom', label: 'Custom' },
];

function ymd(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

export function rangeForPreset(p: DatePreset): DateRange {
  const today = new Date(); today.setHours(0,0,0,0);
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  const last7 = new Date(today); last7.setDate(today.getDate() - 6);
  const firstThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0); // last day prev month
  const firstLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const firstThisYear = new Date(today.getFullYear(), 0, 1);
  switch (p) {
    case 'today':       return { startDate: ymd(today), endDate: ymd(today) };
    case 'yesterday':   return { startDate: ymd(yest), endDate: ymd(yest) };
    case 'last-7-days': return { startDate: ymd(last7), endDate: ymd(today) };
    case 'last-month':  return { startDate: ymd(firstLastMonth), endDate: ymd(lastMonthEnd) };
    case 'this-month':  return { startDate: ymd(firstThisMonth), endDate: ymd(today) };
    case 'this-year':   return { startDate: ymd(firstThisYear), endDate: ymd(today) };
    case 'custom':      return { startDate: ymd(today), endDate: ymd(today) };
  }
}

export function presetLabel(p: DatePreset) {
  return PRESETS.find(x => x.id === p)?.label ?? 'Custom';
}

export function DateRangePicker({ value, onChange, defaultPreset = 'today', size = 'md', className = '' }: Props) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<DatePreset>(defaultPreset);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 288 });

  // Outside-click closes — exclude BOTH the wrapper AND the portal panel since the portal lives
  // outside the wrapRef DOM subtree (same pattern as MultiSelectFilter / SearchableSelect fixes).
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (wrapRef.current && wrapRef.current.contains(tgt)) return;
      if (popRef.current && popRef.current.contains(tgt)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Compute portal position from trigger rect — fixed positioning escapes any overflow:hidden parent
  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const recompute = () => {
      const r = wrapRef.current!.getBoundingClientRect();
      const popWidth = 288; // w-72
      const overflowRight = r.left + popWidth > window.innerWidth - 8;
      const left = overflowRight ? Math.max(8, r.right - popWidth) : r.left;
      setPos({ top: r.bottom + 4, left, width: popWidth });
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [open]);

  const padding = size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm';

  function applyPreset(p: DatePreset) {
    setPreset(p);
    if (p === 'custom') return; // wait for user to pick dates
    const range = rangeForPreset(p);
    onChange({ ...range, preset: p });
    setOpen(false);
  }

  function setCustomDate(key: 'startDate' | 'endDate', v: string) {
    setPreset('custom');
    onChange({ ...value, [key]: v, preset: 'custom' });
  }

  // Display label
  const label = preset === 'custom'
    ? `${value.startDate || '…'} → ${value.endDate || '…'}`
    : presetLabel(preset);

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`${padding} bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2 w-full justify-between`}>
        <div className="flex items-center gap-1.5">
          <Calendar size={14} className="text-slate-400" />
          <span className="text-slate-700 truncate">{label}</span>
        </div>
        <ChevronDown size={14} className="text-slate-400" />
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 10050 }}
          className="bg-white border border-slate-200 rounded-lg shadow-xl p-2">
          <div className="space-y-0.5 mb-1">
            {PRESETS.map(p => (
              <button key={p.id} type="button" onClick={() => applyPreset(p.id)}
                className={`w-full text-left px-3 py-1.5 text-sm rounded transition ${preset === p.id ? 'bg-primary-50 text-primary font-medium' : 'hover:bg-slate-50 text-slate-700'}`}>
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="border-t border-slate-100 pt-2 space-y-2">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 block">From</label>
                <input type="date" value={value.startDate} onChange={(e) => setCustomDate('startDate', e.target.value)} className="input h-8 text-sm w-full" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 block">To</label>
                <input type="date" value={value.endDate} onChange={(e) => setCustomDate('endDate', e.target.value)} className="input h-8 text-sm w-full" />
              </div>
              <button type="button" onClick={() => setOpen(false)} className="w-full px-3 py-1.5 bg-primary text-white text-sm rounded">Apply</button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
